import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import { searchSite } from "../data/searchIndex";
import { SITE_NAME } from "../config/site";

export default function Recherche() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";
  const [input, setInput] = useState(q);
  const results = searchSite(q);

  useEffect(() => {
    setInput(q);
  }, [q]);

  const submit = (e) => {
    e.preventDefault();
    setParams(input.trim() ? { q: input.trim() } : {});
  };

  return (
    <>
      <Seo
        title={q ? `Recherche : ${q} – ${SITE_NAME}` : `Recherche – ${SITE_NAME}`}
        description="Recherchez dans l'ensemble des guides, articles et définitions du site."
        path="/recherche"
        robots="noindex, follow"
      />
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12">
        <div className="max-w-[760px]">
          <Breadcrumbs items={[{ label: "Recherche" }]} />
          <h1 className="font-display text-3xl font-bold tracking-tight text-[#0B1F3A] sm:text-4xl">
            Recherche
          </h1>

          <form onSubmit={submit} className="relative mt-6" role="search">
            <Search
              className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
              aria-hidden="true"
            />
            <input
              type="search"
              data-testid="search-page-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Rechercher dans les guides, articles et le glossaire..."
              aria-label="Rechercher dans le site"
              className="w-full rounded-full border border-slate-300 bg-white py-3 pl-12 pr-4 text-base shadow-sm transition-colors focus:border-[#2BB08A]"
            />
          </form>

          {q.length >= 2 && (
            <p className="mt-5 text-sm text-slate-500" aria-live="polite" data-testid="search-count">
              {results.length} résultat{results.length > 1 ? "s" : ""} pour{" "}
              <strong className="text-[#0B1F3A]">« {q} »</strong>
            </p>
          )}

          {q.length >= 2 && results.length === 0 && (
            <div className="mt-6 rounded-xl bg-[#F3F5F8] p-6 text-[15px] text-slate-600">
              Aucun résultat. Essayez un autre terme, par exemple « cote », « bonus », « ANJ »
              ou « combiné ».
            </div>
          )}

          <ul className="mt-6 space-y-3" data-testid="search-results">
            {q.length >= 2 &&
              results.map((r, i) => (
                <li key={`${r.path}-${i}`}>
                  <Link
                    to={r.path}
                    data-testid={`search-page-result-${i}`}
                    className="block rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md"
                  >
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-[#2BB08A]">
                      {r.category}
                    </p>
                    <p className="mt-1 font-display text-base font-semibold text-[#0B1F3A]">
                      {r.title}
                    </p>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{r.excerpt}</p>
                  </Link>
                </li>
              ))}
          </ul>
        </div>
      </div>
    </>
  );
}
